import React from 'react';
import PropTypes from 'prop-types';
import NoteCards from './NoteCards';
import * as useFetchNotesModule from './useFetchNotes';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock Data
const generateMockNotes = (count) => {
  const notes = [];
  for (let i = 1; i <= count; i += 1) {
    notes.push({
      id: i,
      site_name: `Site ${i}`,
      timestamp: `2024-11-${String(i).padStart(2, '0')}`,
      title: `Note Title ${i}`,
      content: 'This is a sample note content that provides a brief description or details about the note.',
      status: i % 2 === 0 ? 'Open' : 'Closed',
      priorityCardColor: (() => {
        if (i % 3 === 0) return 'high';
        if (i % 2 === 0) return 'medium';
        return 'low';
      })(),
    });
  }
  return notes;
};

const mockNotes = generateMockNotes(9);

// Mock useFetchNotes
// eslint-disable-next-line no-import-assign, import/namespace
useFetchNotesModule.default = () => {
  return [mockNotes, () => {}];
};

export default {
  title: 'Components/Notes/NoteCards',
  component: NoteCards,
  parameters: {
    layout: 'left',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `NoteCards` component displays note records in a visually appealing card format. It enables users to review note details in a gallery-style layout.\n\n',
      // When & how to use it?
      '\nUse the `NoteCards` component when a card/gallery-style representation of note records is needed. It provides a responsive grid layout to display multiple note cards.\n\n',
      // Current Usage
      '\nThe `NoteCards` component is currently used on the **[Notes page](http://localhost:6006/?path=/docs/pages-notespage--docs) when the user switches to the cards/gallery view. It allows users to review note details in a grid layout.\n\n',
      // Related Components
      `\n- **[Note](http://localhost:6006/?path=/docs/components-notes-notecard--docs)**: Represents individual cards within the NoteCards component.
       \n- **useFetchNotes** (app/javascript/src/componentsV2/Notes/useFetchNotes.js): Custom hook for fetching note data.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'Organization ID used to fetch note records for the cards.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    params: {
      control: { type: 'object' },
      description: 'Search and filter parameters for fetching data dynamically.\n\n',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    currentPage: {
      control: { type: 'number' },
      description: 'Current page number for paginated data.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    itemsPerPage: {
      control: { type: 'number' },
      description: 'Number of items displayed per page.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    setTotalNotes: {
      control: { type: 'function' },
      description: 'Callback function to set the total number of notes for pagination.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    onClick: {
      control: { type: 'function' },
      description: 'Callback function triggered when a note card is clicked.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

const NoteCardsTemplate = ({
  organizationId,
  params,
  currentPage,
  itemsPerPage,
  setTotalNotes,
  onClick,
}) => (
  <NoteCards
    organizationId={organizationId}
    params={params}
    currentPage={currentPage}
    itemsPerPage={itemsPerPage}
    setTotalNotes={setTotalNotes}
    onClick={onClick}
  />
);

NoteCardsTemplate.propTypes = {
  organizationId: PropTypes.number.isRequired,
  params: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  setTotalNotes: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

const Template = NoteCardsTemplate.bind({});

export const DefaultNoteCards = Template.bind({});
DefaultNoteCards.args = {
  organizationId: 1,
  params: {},
  currentPage: 1,
  itemsPerPage: 9,
  setTotalNotes: () => { },
};
DefaultNoteCards.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when the user toggles to cards/gallery view on the **[Notes page](http://localhost:6006/?path=/docs/pages-notespage--docs) to display note records in a grid layout. In Storybook, dummy data is displayed for component review purposes because API calls are not possible.\n\n',
  // Where is it currently used on the product?
  '\nThe `NoteCards` component is currently used on the NotesPage for displaying note records in a card/gallery format.\n\n',
);
